import { useCallback, useEffect, useRef } from "react"
import type { ReactNode, RefObject } from "react"
import { Box, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"

export type KellyPaneLayout = "one-column" | "two-column"

const PAGE_SX = { position: "absolute", inset: 0, zIndex: 1, display: "flex", flexDirection: "column", bgcolor: "var(--app-bg-paper)" } satisfies SxProps<Theme>
const HEADER_SX = { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, px: 2, py: 1, borderBottom: 1, borderColor: "divider" } satisfies SxProps<Theme>
const TITLE_SX = { fontWeight: 700 } satisfies SxProps<Theme>

interface KellyPanePageProps {
  title: string
  testId: string
  children?: ReactNode
  /** Rendered before the title, e.g. a back control. */
  headerStart?: ReactNode
  /** Takes focus instead of the section when it is mounted. */
  focusTargetRef?: RefObject<HTMLElement | null>
  /** Changes when the body's content does; focus that fell to <body> on the swap comes back to the pane. */
  refocusKey?: string
}

export function KellyPanePage({ title, testId, children, headerStart, focusTargetRef, refocusKey }: KellyPanePageProps) {
  const pageRef = useRef<HTMLElement>(null)

  // Every opener unmounts as it fires -- the dock flyout closes over its own row -- so focus lands on
  // <body>, outside the <aside> the dock's Escape ladder is bound to, and the pane loses its keyboard exit.
  const takeFocus = useCallback(() => { (focusTargetRef?.current ?? pageRef.current)?.focus() }, [focusTargetRef])

  useEffect(() => {
    const opener = document.activeElement
    takeFocus()
    return () => {
      const active = document.activeElement
      const focusIsLost = active === null || active === document.body
      const openerStands = opener instanceof HTMLElement && opener !== document.body && opener.isConnected
      if (focusIsLost && openerStands) opener.focus()
    }
  }, [takeFocus])

  useEffect(() => {
    const active = document.activeElement
    if (active === null || active === document.body) takeFocus()
  }, [refocusKey, takeFocus])

  return (
    <Box component="section" ref={pageRef} tabIndex={-1} aria-label={title} sx={PAGE_SX} data-testid={testId}>
      <Box sx={HEADER_SX}>
        {headerStart}
        <Typography component="h2" variant="h6" sx={TITLE_SX}>{title}</Typography>
      </Box>
      {children}
    </Box>
  )
}
